import { format, getDate, parse } from "date-fns"
import type { CalendarItem, PlanItem } from "@/graphql/main/gql/graphql"

const CALENDAR_FORMAT = "yyyy-MM-dd"
const DATE_WITH_OFFSET_FORMAT = "yyyy-MM-dd HH:mm:ss XXX"
// Date-only strings (the shape the GraphQL `Date` scalar serializes to —
// see cmd/public-gql/.../types/date.go MarshalDate). new Date("YYYY-MM-DD")
// parses those as UTC midnight per the ECMAScript spec, which shifts the
// calendar day for non-server timezones — a Sunday workout looks like
// Saturday for a user in the US. Parse with a time component instead so
// JS treats it as local midnight and the day stays put.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Converts a value to a Date safely. Use this instead of `new Date(value)`
 * anywhere the input may be a server-returned `YYYY-MM-DD` string — the
 * native constructor parses those as UTC midnight, shifting the calendar
 * day for users outside the server timezone.
 */
/**
 * First and last entry of a non-empty list of dates. A week's date list is never
 * empty, so an empty one here is a caller bug, not a state to render.
 */
export const dateSpan = <T>(dates: readonly T[]): [T, T] => {
  const first = dates[0]
  const last = dates[dates.length - 1]
  if (first === undefined || last === undefined) throw new RangeError("dateSpan: empty date list")
  return [first, last]
}

export const toDate = (value: Date | string | number): Date => {
  if (value instanceof Date) {
    return value
  }
  if (typeof value === "string" && DATE_ONLY_RE.test(value)) {
    return new Date(`${value}T00:00:00`)
  }

  return new Date(value)
}

export const parseDateWithOffset = (value: string): Date => {
  return parse(value, DATE_WITH_OFFSET_FORMAT, new Date())
}

/**
 * Formats a date for calendar use in YYYY-MM-DD format
 */
export const formatDateForCalendar = (date: Date | string | number): string => {
  return format(toDate(date), CALENDAR_FORMAT)
}

/**
 * A form date as the API's `yyyy-MM-dd`, or undefined when there is none or it does not parse.
 * Settings and onboarding send their dates (birthday, target dates) through it. A date-only string
 * keeps its day: through `new Date` it was UTC midnight, and west of UTC every save moved it a day back.
 */
export const formatISODate = (value?: string | Date | null): string | undefined => {
  if (!value) {
    return undefined
  }
  const dateObj = toDate(value)
  if (Number.isNaN(dateObj.getTime())) {
    return undefined
  }
  return format(dateObj, CALENDAR_FORMAT)
}

/**
 * Gets the zero-based day of month (0-30) for plan items
 */
export const getDiffDate = (date: Date | string | number): number => {
  return getDate(toDate(date)) - 1
}

/**
 * Type guard to check if an item is a CalendarItem
 */
export const isCalendarItem = (item: unknown): item is CalendarItem => {
  if (item === null || typeof item !== "object") return false
  const record = item as Record<string, unknown>
  return "date" in record && typeof record.date === "string"
}

/**
 * Type guard to check if an item is a PlanItem
 */
export const isPlanItem = (item: unknown): item is PlanItem => {
  if (item === null || typeof item !== "object") return false
  const record = item as Record<string, unknown>
  return "diffDate" in record && typeof record.diffDate === "number"
}

/**
 * Safely formats a date value, returning fallback string for invalid dates.
 * Prevents RangeError: Invalid time value from crashing the app.
 */
export const safeFormatDate = (date: Date | string | null | undefined, pattern: string, fallback = ""): string => {
  if (!date) return fallback
  try {
    const dateObj = typeof date === "string" ? toDate(date) : date
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return fallback
    return format(dateObj, pattern)
  } catch {
    return fallback
  }
}

/**
 * Safely converts a value to a finite number, returning fallback for NaN/Infinity.
 */
export const safeFiniteNumber = (value: unknown, fallback = 0): number => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

/*  Правильный формат даты YYYY-MM-DD */
export function parseDateWithoutTimezone(date: string) {
  if (!date) {
    return null
  }

  return new Date(`${date}T00:00:00`)
}
